import { useSearchParams, Link } from 'react-router-dom';
import { Stack, Text, Group, Image, Rating, Divider, Anchor, Collapse, UnstyledButton } from '@mantine/core';
import { IconChevronDown, IconChevronUp, IconPackage, IconBulb } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { productsService } from '../../services/products';

const WRITING_TIPS = [
  'Be specific — "the lid cracked after 2 weeks" beats "bad quality".',
  'Mention who it\'s best for — families, solo use, gift-giving, etc.',
  'Compare to alternatives if you\'ve tried them.',
  'Include how long you\'ve used it — one-time use vs. months of use read very differently.',
  'Note where you bought it and roughly what you paid.',
];

function WritingTips({ collapsible }: { collapsible?: boolean }) {
  const [open, setOpen] = useState(!collapsible);
  return (
    <Stack gap="xs">
      {collapsible ? (
        <UnstyledButton onClick={() => setOpen((o) => !o)}>
          <Group gap="xs">
            <IconBulb size={16} />
            <Text size="sm" fw={600}>Writing tips</Text>
            {open ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
          </Group>
        </UnstyledButton>
      ) : (
        <Group gap="xs">
          <IconBulb size={16} />
          <Text size="sm" fw={600}>Writing tips</Text>
        </Group>
      )}
      <Collapse in={open}>
        <Stack gap={6}>
          {WRITING_TIPS.map((tip, i) => (
            <Text key={i} size="xs" c="dimmed">• {tip}</Text>
          ))}
        </Stack>
      </Collapse>
    </Stack>
  );
}

export function CreateReviewSidebar() {
  const [searchParams] = useSearchParams();
  const productId = searchParams.get('productId');

  const { data: product } = useQuery({
    queryKey: ['product', productId],
    queryFn: () => productsService.getById(productId!),
    staleTime: 10 * 60 * 1000,
    enabled: !!productId,
  });

  return (
    <Stack gap="lg">
      {product && (
        <>
          <Stack gap="sm">
            <Text size="sm" fw={600}>Reviewing</Text>
            <Group gap="sm" wrap="nowrap">
              {product.imageUrl ? (
                <Image src={product.imageUrl} w={48} h={48} fit="contain" radius="sm" style={{ flexShrink: 0 }} />
              ) : (
                <Group w={48} h={48} justify="center" align="center" style={{ flexShrink: 0, borderRadius: 'var(--mantine-radius-sm)', background: 'var(--mantine-color-default)' }}>
                  <IconPackage size={20} color="var(--mantine-color-dimmed)" />
                </Group>
              )}
              <Stack gap={2} style={{ minWidth: 0 }}>
                <Text size="sm" fw={600} lineClamp={2}>{product.name}</Text>
                {product.brand && <Text size="xs" c="dimmed">{product.brand}</Text>}
                {product.averageRating != null && (
                  <Group gap={4}>
                    <Rating value={product.averageRating} fractions={2} readOnly size="xs" />
                    <Text size="xs" c="dimmed">({product.reviewCount})</Text>
                  </Group>
                )}
              </Stack>
            </Group>
          </Stack>
          <Divider />
        </>
      )}

      <WritingTips collapsible={!!product} />

      <Divider />
      <Stack gap={4}>
        <Text size="xs" c="dimmed">
          <Anchor component={Link} to="/privacy" size="xs" c="dimmed">Privacy</Anchor>
          {' · '}
          <Anchor component={Link} to="/terms" size="xs" c="dimmed">Terms</Anchor>
        </Text>
        <Text size="xs" c="dimmed">© {new Date().getFullYear()} CartCritic</Text>
      </Stack>
    </Stack>
  );
}
